import type { Block, BlockType } from '..'
import { TreeManager } from '@basehub/utils/tree/index'
import { invariant } from '@epic-web/invariant'

// ----------------------------------------------------------------------------------------------------------
//                                                📦 Utils to check if EXISTS
// ----------------------------------------------------------------------------------------------------------

export const checkIfApiNameExists = ({
  apiName,
  blockTreeManager,
  blockId,
}: {
  apiName: string
  blockTreeManager: TreeManager<Block, NonNullable<unknown>>
  blockId: string
}) => {
  const block = blockTreeManager.getNode(blockId, {
    fallbackKey: 'sourceBlockId',
  })
  const parent = blockTreeManager.getNode(block?.parentKey ?? '', {
    fallbackKey: 'sourceBlockId',
  })

  invariant(parent, 'Parent not found')
  invariant(block, 'Block not found')

  const children = blockTreeManager.getChildrenKeys(parent.key)
  invariant(children, 'Children not found')

  return children.some((key) => {
    const c = blockTreeManager.getNode(key, { required: true }).value
    return (
      c.id !== blockId && 'apiName' in c && c.apiName && c.apiName === apiName
    )
  })
}

export const checkIfSlugExists = ({
  slug,
  blockTreeManager,
  blockId,
}: {
  slug: string
  blockTreeManager: TreeManager<Block, NonNullable<unknown>>
  blockId: string
}) => {
  const block = blockTreeManager.getNode(blockId, {
    fallbackKey: 'sourceBlockId',
  })
  const parent = blockTreeManager.getNode(block?.parentKey ?? '', {
    fallbackKey: 'sourceBlockId',
  })

  invariant(parent, 'Parent not found')
  invariant(block, 'Block not found')

  const children = blockTreeManager.getChildrenKeys(parent.key)
  invariant(children, 'Children not found')

  return children.some((key) => {
    const c = blockTreeManager.getNode(key, { required: true }).value
    return c.id !== blockId && 'slug' in c && c.slug && c.slug === slug
  })
}

// ----------------------------------------------------------------------------------------------------------
//                                                👨‍👩‍👧 Check family of block
// ----------------------------------------------------------------------------------------------------------

export const isDescendantOf = ({
  nodeId,
  treeManager,
  ignoreTypes,
  descendantOf,
}: {
  nodeId: string
  treeManager: TreeManager<Block, NonNullable<unknown>>
  ignoreTypes?: BlockType[]
  descendantOf: BlockType[] | BlockType
}): boolean => {
  const node = treeManager.getNode(nodeId, { fallbackKey: 'sourceBlockId' })
  if (!node) return false
  if (ignoreTypes && ignoreTypes.includes(node.value.type)) return false
  if (Array.isArray(descendantOf)) {
    if (descendantOf.includes(node.value.type)) return true
  } else {
    if (node.value.type === descendantOf) return true
  }
  if (!node.parentKey) return false
  return isDescendantOf({
    nodeId: node.parentKey,
    treeManager,
    ignoreTypes,
    descendantOf,
  })
}

export * from './helpers'
export * from './recursive'
